import { z } from 'zod';
import { containsProfanity } from './profanityFilter';

/**
 * Profanity validation refinement - adds profanity check to any string schema
 */
export const noProfanity = <T extends z.ZodString>(schema: T) =>
	schema.refine((val) => !containsProfanity(val), {
		message: 'Please use appropriate language'
	});

/**
 * Create a string schema with profanity check built-in
 */
export const cleanStringSchema = (options?: { min?: number; max?: number }) => {
	let schema = z.string().trim();
	if (options?.min !== undefined) {
		schema = schema.min(options.min, { message: `Must be at least ${options.min} characters` });
	}
	if (options?.max !== undefined) {
		schema = schema.max(options.max, { message: `Must be less than ${options.max} characters` });
	}
	return schema.refine((val) => !containsProfanity(val), {
		message: 'Please use appropriate language'
	});
};

export const emailSchema = z
	.string()
	.trim()
	.email({ message: 'Invalid email address' })
	.max(255, { message: 'Email too long' });

const BLOCKED_REPLY_DOMAINS = new Set([
	'example.com',
	'example.net',
	'example.org',
	'test.com',
	'test.test',
	'invalid.com',
	'cardsimp.com'
]);

const BLOCKED_REPLY_TLDS = new Set(['invalid', 'test', 'example', 'localhost']);

const BLOCKED_REPLY_LOCAL_PARTS = /^(test|testing|example|sample|demo|fake|noreply|no-reply|notify)([._+-].*)?$/i;

export function getReplyEmailError(value: string): string | null {
	const parsed = emailSchema.safeParse(value);
	if (!parsed.success) return parsed.error.issues[0]?.message ?? 'Invalid email address';

	const normalized = parsed.data.toLowerCase();
	const [localPart, domain] = normalized.split('@');
	const tld = domain.split('.').pop() ?? '';

	if (BLOCKED_REPLY_DOMAINS.has(domain) || BLOCKED_REPLY_TLDS.has(tld) || BLOCKED_REPLY_LOCAL_PARTS.test(localPart)) {
		return 'Use a real inbox you can receive replies at.';
	}

	return null;
}

export const replyEmailSchema = emailSchema.refine((value) => !getReplyEmailError(value), {
	message: 'Use a real inbox you can receive replies at.'
});

export const passwordSchema = z
	.string()
	.min(8, { message: 'Password must be at least 8 characters' })
	.max(128, { message: 'Password too long' });

export const usernameSchema = z
	.string()
	.trim()
	.min(3, { message: 'Username must be at least 3 characters' })
	.max(30, { message: 'Username must be less than 30 characters' })
	.regex(/^[a-zA-Z0-9_-]+$/, {
		message: 'Username can only contain letters, numbers, underscores, and hyphens'
	});

export const signUpSchema = z.object({
	email: emailSchema,
	password: passwordSchema
});

// Sign-in only requires *some* password — legacy accounts may pre-date the
// 8-char minimum. Strict length enforcement stays on sign-up & password reset.
export const signInPasswordSchema = z
	.string()
	.min(1, { message: 'Enter your password' })
	.max(128, { message: 'Password too long' });

export const signInSchema = z.object({
	email: emailSchema,
	password: signInPasswordSchema
});

export const passwordUpdateSchema = z
	.object({
		newPassword: passwordSchema,
		confirmPassword: passwordSchema
	})
	.refine((data) => data.newPassword === data.confirmPassword, {
		message: 'Passwords do not match',
		path: ['confirmPassword']
	});

export const deckNameSchema = z
	.string()
	.trim()
	.min(1, { message: 'Deck name is required' })
	.max(30, { message: 'Deck name must be 30 characters or less' });

export const contactFormSchema = z.object({
	name: z
		.string()
		.trim()
		.min(1, { message: 'Name is required' })
		.max(100, { message: 'Name must be less than 100 characters' }),
	email: emailSchema,
	subject: z.enum(
		['Question / Support', 'Business', 'Bug Report', 'Feedback', 'Feature Request'],
		{ required_error: 'Please select a subject' }
	),
	message: z
		.string()
		.trim()
		.min(1, { message: 'Message is required' })
		.max(2000, { message: 'Message must be less than 2000 characters' })
});

// Phone number schema (US format)
export const phoneSchema = z
	.string()
	.trim()
	.regex(/^(\+1)?[\s.-]?\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}$/, {
		message: 'Please enter a valid US phone number'
	})
	.optional()
	.or(z.literal(''));

// Client-side phone sanitization - keeps only digits
export function sanitizePhone(input: string): string {
	if (!input) return '';
	// Remove all non-digit characters except + at the start
	const digits = input.replace(/[^\d]/g, '');
	// Format as (XXX) XXX-XXXX if 10 digits
	if (digits.length === 10) {
		return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
	}
	// If 11 digits starting with 1, format as +1 (XXX) XXX-XXXX
	if (digits.length === 11 && digits.startsWith('1')) {
		return `+1 (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
	}
	return input;
}

// Client-side username sanitization
export function sanitizeUsername(input: string): string {
	if (!input) return '';

	// Remove any characters not allowed by usernameSchema
	return input
		.replace(/[^\w\-]/g, '') // Keep only alphanumeric, underscore, hyphen
		.slice(0, 30) // Max length
		.trim();
}
